import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Plus, Trash2, Edit, Save, FileText, Printer, Eye, Settings, Search } from 'lucide-react';
import { toast } from 'sonner';
import { confirmDialog } from '../ui/confirm-dialog';

const ServiceLifecycleQuotationRequest = ({ user, axiosInstance }) => {
  const [quotations, setQuotations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentQuotation, setCurrentQuotation] = useState(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const componentRef = useRef();
  
  // Template State
  const [templateData, setTemplateData] = useState({
    salutation: "Dear Ma'am/Sir,",
    intro_text: "This proposal incorporates information about your key requirements, detailed pricing information.",
    notes: 'Free delivery and installation.\nLifetime free onsite service warranty, monthly preventive maintenance, free check-up, and trouble shooting of the units guaranteed that all consumables are directly purchased to us.',
    delivery_terms: 'Delivery lead time shall be 1 - 3 days upon receipt of the Purchase Order, provided that the required spareparts are available; otherwise, the estimated delivery will be 30 - 60 days.',
    closing_text: 'Thank you for partnering with iSource Asia Business Solutions Corporation.',
    prepared_by: '',
    prepared_by_title: 'Technical Coordinator',
    prepared_by_email: '',
    signature_url: ''
  });

  // Form State
  const [formData, setFormData] = useState({
    date: new Date().toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' }),
    client_name: '',
    department: '',
    attention: '',
    salutation: templateData.salutation,
    intro_text: templateData.intro_text,
    machine_model: '',
    serial_number: '',
    items: [],
    notes: templateData.notes,
    delivery_terms: templateData.delivery_terms,
    closing_text: templateData.closing_text,
    prepared_by: user?.full_name || '',
    prepared_by_title: templateData.prepared_by_title,
    prepared_by_email: user?.email || '',
    signature_url: templateData.signature_url || '',
    prepared_by_mobile: '',
    conforme_signature: '',
    status: 'Pending'
  });

  useEffect(() => {
    fetchQuotations();
    fetchTemplate();
  }, []);

  const fetchQuotations = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/quotation-requests');
      setQuotations(response.data);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      toast.error('Failed to load quotations');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTemplate = async () => {
    try {
      const response = await axiosInstance.get('/quotation-template');
      if (response.data) {
        const fetchedTemplate = {
          salutation: response.data.salutation || templateData.salutation,
          intro_text: response.data.intro_text || templateData.intro_text,
          notes: response.data.notes || templateData.notes,
          delivery_terms: response.data.delivery_terms || templateData.delivery_terms,
          closing_text: response.data.closing_text || templateData.closing_text,
          prepared_by: response.data.prepared_by || '',
          prepared_by_title: response.data.prepared_by_title || templateData.prepared_by_title,
          prepared_by_email: response.data.prepared_by_email || '',
          signature_url: response.data.signature_url || ''
        };
        setTemplateData(fetchedTemplate);
        
        // If we're in "New" mode (no current quotation selected), update the form with new template data immediately
        if (!currentQuotation) {
          setFormData(prev => ({
            ...prev,
            ...fetchedTemplate,
            prepared_by: fetchedTemplate.prepared_by || user?.full_name || '', // Use template name if set, else user name
            prepared_by_email: fetchedTemplate.prepared_by_email || user?.email || '', // Prefer template email if set, else user email
            signature_url: fetchedTemplate.signature_url || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      await axiosInstance.put('/quotation-template', templateData);
      toast.success('Template saved successfully');
      setIsTemplateModalOpen(false);
      
      // Update form if in "New" mode
      if (!currentQuotation) {
        setFormData(prev => ({
          ...prev,
          ...templateData,
          prepared_by: templateData.prepared_by || user?.full_name || '',
          prepared_by_email: templateData.prepared_by_email || prev.prepared_by_email,
          signature_url: templateData.signature_url || prev.signature_url
        }));
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post('/upload-signature', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setTemplateData(prev => ({ ...prev, signature_url: response.data.url }));
      toast.success('Signature uploaded successfully');
    } catch (error) {
      console.error('Error uploading signature:', error);
      toast.error('Failed to upload signature');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        { qty: 1, unit: 'pc', description: '', item_code: '', unit_price: 0, total: 0 }
      ]
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Auto calculate total
    if (field === 'qty' || field === 'unit_price') {
      const qty = parseFloat(newItems[index].qty) || 0;
      const price = parseFloat(newItems[index].unit_price) || 0;
      newItems[index].total = qty * price;
    }
    
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const calculateGrandTotal = () => {
    return formData.items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const handleSave = async () => {
    if (!formData.client_name || !formData.machine_model) {
      toast.error('Please fill in Client Name and Machine Model');
      return;
    }

    // Sanitize items to ensure numeric fields are numbers
    const sanitizedItems = formData.items.map(item => ({
      ...item,
      qty: parseInt(item.qty) || 0,
      unit_price: parseFloat(item.unit_price) || 0,
      total: parseFloat(item.total) || 0
    }));

    try {
      const payload = {
        ...formData,
        items: sanitizedItems,
        total_amount: calculateGrandTotal()
      };

      if (currentQuotation) {
        await axiosInstance.put(`/quotation-requests/${currentQuotation.id}`, payload);
        toast.success('Quotation updated successfully');
      } else {
        await axiosInstance.post('/quotation-requests', payload);
        toast.success('Quotation created successfully');
      }
      
      fetchQuotations();
      // Don't clear form immediately to allow further edits or printing
    } catch (error) {
      console.error('Error saving quotation:', error);
      toast.error('Failed to save quotation');
    }
  };

  const handleEdit = (quotation) => {
    setCurrentQuotation(quotation);
    // Parse date to Month DD, YYYY for input
    const dateStr = new Date(quotation.date).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
    setFormData({
      ...quotation,
      date: dateStr,
      // Ensure arrays are initialized
      items: quotation.items || []
    });
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
        title: "Delete Quotation",
        description: "Are you sure you want to delete this quotation?",
        variant: "destructive"
    });
    if (!confirmed) return;

    try {
      await axiosInstance.delete(`/quotation-requests/${id}`);
      toast.success('Quotation deleted');
      fetchQuotations();
      if (currentQuotation?.id === id) {
        handleNew();
      }
    } catch (error) {
      console.error('Error deleting quotation:', error);
      toast.error('Failed to delete quotation');
    }
  };

  const handleNew = () => {
    setCurrentQuotation(null);
    setFormData({
      date: new Date().toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' }),
      client_name: '',
      department: '',
      attention: '',
      salutation: templateData.salutation,
      intro_text: templateData.intro_text,
      machine_model: '',
      serial_number: '',
      items: [],
      notes: templateData.notes,
      delivery_terms: templateData.delivery_terms,
      closing_text: templateData.closing_text,
      prepared_by: templateData.prepared_by || user?.full_name || '',
      prepared_by_title: templateData.prepared_by_title,
      prepared_by_email: templateData.prepared_by_email || user?.email || '',
      prepared_by_mobile: '',
      conforme_signature: '',
      status: 'Pending'
    });
  };

  const filteredQuotations = quotations.filter(q => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (q.client_name?.toLowerCase() || "").includes(searchLower) ||
      (q.attention?.toLowerCase() || "").includes(searchLower) ||
      (q.machine_model?.toLowerCase() || "").includes(searchLower) ||
      (q.serial_number?.toLowerCase() || "").includes(searchLower)
    );
  });

  const handlePrint = () => {
    const printContent = componentRef.current;
    if (!printContent) return;

    // Clone the element to manipulate it for print
    const clone = printContent.cloneNode(true);
    
    // Copy values from original inputs to cloned inputs and replace with div/span for better printing
    const originalInputs = printContent.querySelectorAll('input, textarea, select');
    const clonedInputs = clone.querySelectorAll('input, textarea, select');

    for (let i = 0; i < originalInputs.length; i++) {
        const originalInput = originalInputs[i];
        const clonedInput = clonedInputs[i];
        
        if (!clonedInput) continue;

        // Skip inputs that are hidden in print
        if (originalInput.classList.contains('print:hidden')) {
            continue;
        }

        if (originalInput.type === 'checkbox' || originalInput.type === 'radio') {
            clonedInput.checked = originalInput.checked;
            if (originalInput.checked) clonedInput.setAttribute('checked', '');
        } else {
            // Create a div to replace the input for better printing (handles text wrapping, etc.)
            const container = document.createElement('div');
            container.textContent = originalInput.value;
            
            // Copy relevant styles
            const computedStyle = window.getComputedStyle(originalInput);
            container.style.display = 'flex';
            container.style.alignItems = 'center';
            container.style.justifyContent = computedStyle.textAlign === 'center' ? 'center' : (computedStyle.textAlign === 'right' ? 'flex-end' : 'flex-start');
            container.style.width = '100%';
            // Use minHeight to allow expansion for textareas, but match height for single lines
            container.style.minHeight = computedStyle.height; 
            container.style.padding = computedStyle.padding;
            container.style.fontFamily = computedStyle.fontFamily;
            container.style.fontSize = computedStyle.fontSize;
            container.style.fontWeight = computedStyle.fontWeight;
            container.style.color = computedStyle.color;
            container.style.backgroundColor = 'transparent';
            container.style.whiteSpace = 'pre-wrap'; // For textarea content
            container.style.lineHeight = computedStyle.lineHeight;
            // Preserve borders if they exist (some inputs might have bottom borders)
            if (computedStyle.borderBottomWidth !== '0px') {
                container.style.borderBottom = computedStyle.borderBottom;
            }
            
            if (clonedInput.parentNode) {
                clonedInput.parentNode.replaceChild(container, clonedInput);
            }
        }
    }
    
    // Remove elements that should be hidden in print (like Add Item button and hidden inputs)
    // We do this AFTER copying values to ensure indices of originalInputs and clonedInputs match
    const hiddenElements = clone.querySelectorAll('.print\\:hidden');
    hiddenElements.forEach(el => el.remove());

    // Remove scaling and margins from pages in the clone
    const pages = clone.children;
    for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        page.style.transform = 'none'; // Remove scaling
        page.style.margin = '0 auto'; // Center it
        page.style.boxShadow = 'none';
        page.style.marginBottom = '0';
        // Ensure width is correct
        page.style.width = '210mm';
        page.style.minHeight = '297mm';
        page.style.overflow = 'hidden';
    }

    const printContents = clone.innerHTML;

    // Create a new window for printing to avoid messing with the current page state
    const printWindow = window.open('', '_blank');
    
    // Copy all styles from the main document to the new window
    const styles = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .map(rule => rule.cssText)
            .join('');
        } catch (e) {
          console.log('Access to stylesheet denied', e);
          return '';
        }
      })
      .join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Quotation</title>
          <style>
            ${styles}
            body { margin: 0; padding: 0; background: white; }
            @media print {
              @page { size: A4; margin: 0; }
              body { -webkit-print-color-adjust: exact; }
              /* Ensure the content fits the page width */
              html, body { width: 210mm; }
            }
          </style>
          <script src="https://cdn.tailwindcss.com"></script>
        </head>
        <body>
          <div class="flex flex-col items-center">
            ${printContents}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load before printing
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Pagination Constants
  const ITEMS_PER_FIRST_PAGE = 10;
  const ITEMS_PER_SUBSEQUENT_PAGE = 13;

  const getPages = () => {
    const items = formData.items;
    const pages = [];
    
    // Always create at least one page
    if (items.length === 0) {
      pages.push({
        items: [],
        isFirstPage: true,
        isLastPage: true
      });
      return pages;
    }

    // First page logic
    const firstPageItems = items.slice(0, ITEMS_PER_FIRST_PAGE);
    const remainingItems = items.slice(ITEMS_PER_FIRST_PAGE);
    
    pages.push({
      items: firstPageItems,
      isFirstPage: true,
      isLastPage: remainingItems.length === 0
    });

    // Subsequent pages logic
    let currentIndex = ITEMS_PER_FIRST_PAGE;
    while (currentIndex < items.length) {
      const pageItems = items.slice(currentIndex, currentIndex + ITEMS_PER_SUBSEQUENT_PAGE);
      pages.push({
        items: pageItems,
        isFirstPage: false,
        isLastPage: currentIndex + ITEMS_PER_SUBSEQUENT_PAGE >= items.length
      });
      currentIndex += ITEMS_PER_SUBSEQUENT_PAGE;
    }

    return pages;
  };

  const renderPage = (pageData, pageIndex) => {
    const { items, isFirstPage, isLastPage } = pageData;
    
    return (
      <div key={pageIndex} className="bg-white shadow-lg p-8 text-sm text-gray-900 font-['Times_New_Roman'] mb-8 last:mb-0" style={{ width: '210mm', minHeight: '297mm', transform: 'scale(0.85)', transformOrigin: 'top center' }}>
        
        {/* Header - Only on First Page */}
        {isFirstPage && (
          <div className="relative pt-8">
            {/* Logo - Absolute Position to not affect vertical flow */}
            <div className="absolute top-0 right-0 w-28 h-auto">
              <img src="https://customer-assets.emergentagent.com/job_hr-system-repair-1/artifacts/au3caisj_iSource%20LOGO.png" alt="iSource Logo" className="object-contain w-full h-full" />
            </div>

            <div className="space-y-2 w-2/3">
              <div className="flex items-center gap-2">
                <span className="w-16 font-bold">Date:</span>
                <Input 
                  type="text" 
                  name="date" 
                  value={formData.date} 
                  onChange={handleInputChange} 
                  className="border-none border-b rounded-none h-auto py-0 px-1 w-40 font-bold focus:ring-0 shadow-none"
                />
              </div>
              
              <div className="mt-2">
                <Input 
                  name="client_name" 
                  value={formData.client_name} 
                  onChange={handleInputChange} 
                  className="border-none font-bold text-lg p-0 h-auto focus:ring-0 shadow-none placeholder:text-transparent"
                />
                {!formData.client_name && <div className="text-gray-300 italic -mt-6 mb-2 pointer-events-none">Client Name / Company</div>}
                
                <Input 
                  name="department" 
                  value={formData.department} 
                  onChange={handleInputChange} 
                  className="border-none italic p-0 h-auto focus:ring-0 shadow-none placeholder:text-transparent"
                />
                {!formData.department && <div className="text-gray-300 italic -mt-6 mb-2 pointer-events-none">(Department)</div>}
              </div>

              <div className="flex items-center gap-2 mt-2">
                <span className="font-bold">Attention:</span>
                <Input 
                  name="attention" 
                  value={formData.attention} 
                  onChange={handleInputChange} 
                  className="border-none border-b rounded-none h-auto py-0 px-1 flex-1 font-bold focus:ring-0 shadow-none placeholder:text-transparent"
                />
                {!formData.attention && <div className="text-gray-300 italic -mt-6 ml-24 pointer-events-none absolute">Procurement Office</div>}
              </div>
            </div>
          </div>
        )}

        {/* Header Spacer for subsequent pages if needed or simple continuation header */}
        {!isFirstPage && (
          <div className="mb-8 text-right text-gray-400 italic">
            Quotation for {formData.client_name || 'Client'} - Page {pageIndex + 1}
          </div>
        )}

        {/* Salutation & Intro - Only on First Page */}
        {isFirstPage && (
          <div className="mb-4 space-y-2 mt-8">
            <div className="flex items-center gap-2">
              <span>Dear</span>
              <Input 
                name="salutation" 
                value={formData.salutation} 
                readOnly
                className="border-none h-auto py-0 px-1 w-40 font-bold focus:ring-0 shadow-none pointer-events-none"
              />
            </div>
            
            <Textarea 
              name="intro_text" 
              value={formData.intro_text} 
              readOnly
              className="border-none resize-none p-0 focus:ring-0 min-h-[40px] shadow-none w-full pointer-events-none"
            />
          </div>
        )}

        {/* Machine Info - Only on First Page */}
        {isFirstPage && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-24">Model:</span>
              <Input 
                name="machine_model" 
                value={formData.machine_model} 
                onChange={handleInputChange} 
                className="border-none border-b border-black rounded-none h-auto py-0 px-1 font-bold w-full focus:ring-0 shadow-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-24 whitespace-nowrap">Serial Number:</span>
              <Input 
                name="serial_number" 
                value={formData.serial_number} 
                onChange={handleInputChange} 
                className="border-none border-b border-black rounded-none h-auto py-0 px-1 w-full focus:ring-0 shadow-none"
              />
            </div>
          </div>
        )}

        {/* Items Table */}
        <div className="mb-4">
          <table className="w-full border-collapse border border-blue-900">
            <thead>
              <tr className="bg-blue-900 text-white text-center">
                <th className="border border-blue-900 p-1 w-16 font-bold align-middle">Qty.</th>
                <th className="border border-blue-900 p-1 w-16 font-bold align-middle">Unit</th>
                <th className="border border-blue-900 p-1 font-bold align-middle">Description</th>
                <th className="border border-blue-900 p-1 w-28 font-bold align-middle">Item Code</th>
                <th className="border border-blue-900 p-1 w-24 font-bold align-middle">Unit Price</th>
                <th className="border border-blue-900 p-1 w-24 font-bold border-r-0 align-middle">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                // Calculate absolute index for correct state updates
                const absoluteIndex = formData.items.indexOf(item);
                return (
                  <tr key={absoluteIndex} className="h-6">
                    <td className="border border-gray-400 p-0 align-middle">
                      <Input 
                        type="number" 
                        value={item.qty} 
                        onChange={(e) => handleItemChange(absoluteIndex, 'qty', e.target.value)} 
                        className="border-none text-right h-full w-full focus:ring-0 shadow-none px-1 bg-transparent"
                      />
                    </td>
                    <td className="border border-gray-400 p-0 align-middle">
                      <Input 
                        value={item.unit} 
                        onChange={(e) => handleItemChange(absoluteIndex, 'unit', e.target.value)} 
                        className="border-none text-center h-full w-full focus:ring-0 shadow-none p-0 bg-transparent"
                      />
                    </td>
                    <td className="border border-gray-400 p-0 align-middle">
                      <Input 
                        value={item.description} 
                        onChange={(e) => handleItemChange(absoluteIndex, 'description', e.target.value)} 
                        className="border-none text-left h-full w-full focus:ring-0 shadow-none px-2 bg-transparent"
                      />
                    </td>
                    <td className="border border-gray-400 p-0 align-middle">
                      <Input 
                        value={item.item_code} 
                        onChange={(e) => handleItemChange(absoluteIndex, 'item_code', e.target.value)} 
                        className="border-none text-right h-full w-full focus:ring-0 shadow-none px-1 bg-transparent"
                      />
                    </td>
                    <td className="border border-gray-400 p-0 align-middle">
                       <div
                         className="text-right px-1 w-full h-full flex items-center justify-end relative group"
                         style={{ isolation: 'isolate' }}
                       >
                         <span className="absolute left-1 opacity-0 group-hover:opacity-100 cursor-text pointer-events-none text-gray-400 text-[10px] print:hidden">Edit</span>
                         <Input 
                            type="number" 
                            value={item.unit_price} 
                            onChange={(e) => handleItemChange(absoluteIndex, 'unit_price', e.target.value)} 
                            className="border-none text-right h-full w-full focus:ring-0 shadow-none p-0 print:hidden"
                            style={{
                              position: 'absolute',
                              inset: 0,
                              opacity: 0,
                              zIndex: 20,
                              background: 'transparent',
                            }}
                            onFocus={(e) => { e.currentTarget.style.opacity = '1'; }}
                            onBlur={(e) => { e.currentTarget.style.opacity = '0'; }}
                          />
                         <span className={item.unit_price ? "" : "text-transparent"} style={{ position: 'relative', zIndex: 10 }}>
                           ₱ {parseFloat(item.unit_price || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                         </span>
                       </div>
                    </td>
                    <td className="border border-gray-400 border-r-0 p-0 align-middle text-right font-medium px-1 relative group">
                      ₱ {item.total.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      <button onClick={() => handleRemoveItem(absoluteIndex)} className="absolute right-[-20px] top-1 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              
              {/* Add button row - Only on Last Page */}
              {isLastPage && (
                <tr className="print:hidden">
                  <td colSpan={6} className="p-1 text-center border border-gray-400 border-t-0 border-r-0">
                    <Button variant="ghost" size="sm" onClick={handleAddItem} className="w-full h-6 text-blue-600">
                      <Plus className="h-3 w-3 mr-1" /> Add Item
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
            {/* Total Row - Only on Last Page */}
            {isLastPage && (
              <tfoot>
                <tr>
                  <td colSpan={5} className="text-right font-bold p-2 border-none">TOTAL AMOUNT</td>
                  <td className="border-b-4 border-double border-black text-right font-bold p-2 border-l-0">
                     ₱ {calculateGrandTotal().toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Footer Content - Only on Last Page */}
        {isLastPage && (
          <>
            {/* Notes & Terms */}
            <div className="space-y-4 mb-4">
              <div>
                <span className="font-bold underline italic">Note:</span>
                <Textarea 
                  name="notes" 
                  value={formData.notes} 
                  readOnly
                  className="border-none resize-none p-0 focus:ring-0 italic min-h-[60px] w-full shadow-none pointer-events-none"
                />
              </div>
              
              <div>
                <span className="font-bold">Delivery Terms:</span>
                <div className="ml-4 italic">
                  <Textarea 
                    name="delivery_terms" 
                    value={formData.delivery_terms} 
                    readOnly
                    className="border-none resize-none p-0 focus:ring-0 min-h-[40px] w-full shadow-none pointer-events-none"
                  />
                </div>
              </div>
              
              <Input 
                name="closing_text" 
                value={formData.closing_text} 
                readOnly
                className="border-none font-bold p-0 focus:ring-0 shadow-none pointer-events-none"
              />
              
              <div className="mt-2">
                <p>For queries and clarifications, please do not hesitate to call us at (02) 8706-4477.</p>
                <p className="mt-2">We take pride in having you as our client and we value the opportunity to be of service to you.</p>
                <p className="mt-4">Yours sincerely,</p>
              </div>
            </div>

            {/* Signatures */}
            <div className="flex justify-between mt-12 items-end">
              <div className="w-1/2 pr-8 relative">
                {/* Overlapping Signature */}
                {formData.signature_url && (
                  <div
                    className="absolute w-48 h-24 pointer-events-none z-10 flex items-center justify-start"
                    style={{ left: '54px', top: '-40px' }}
                  >
                    <img 
                      src={formData.signature_url} 
                      alt="Signature" 
                      crossOrigin="anonymous"
                      className="max-w-full max-h-full object-contain opacity-90 mix-blend-multiply"
                    />
                  </div>
                )}
                
                <Input 
                  name="prepared_by" 
                  value={formData.prepared_by} 
                  readOnly
                  className="border-none font-bold uppercase p-0 h-auto focus:ring-0 shadow-none relative z-20 bg-transparent pointer-events-none"
                />
                <Input 
                  name="prepared_by_title" 
                  value={formData.prepared_by_title} 
                  readOnly
                  className="border-none italic text-sm p-0 h-auto focus:ring-0 shadow-none pointer-events-none"
                />
                <div className="mt-2 text-xs">
                  <div>Email Address: 
                    <Input 
                      name="prepared_by_email" 
                      value={formData.prepared_by_email} 
                      readOnly
                      className="border-none p-0 h-auto w-48 text-xs focus:ring-0 shadow-none inline-block ml-1 pointer-events-none"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    Cell No.: 
                    <Input 
                      name="prepared_by_mobile" 
                      value={formData.prepared_by_mobile} 
                      onChange={handleInputChange} 
                      className="border-none p-0 h-auto w-32 text-xs focus:ring-0 shadow-none"
                    />
                  </div>
                </div>
              </div>
              
              <div className="w-1/2 pl-8">
                <div className="font-bold mb-8">CONFORME:</div>
                <div className="border-b-2 border-black w-full mb-2"></div>
                <div className="text-center">(Client Signature)</div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-[calc(100vh-230px)] gap-4">
      {/* Left Panel - List (30%) */}
      <div
        className="w-[30%] flex flex-col h-full rounded-2xl overflow-hidden"
        style={{
          background: 'var(--nad-s1)',
          border: '1px solid var(--nad-b1)',
        }}
      >
        <div
          className="flex flex-col py-4 px-4 gap-3 border-b"
          style={{
            borderColor: 'var(--nad-b1)',
            background: 'linear-gradient(to bottom, rgba(59,130,246,0.08), rgba(250,204,21,0.04))',
          }}
        >
          <div className="flex flex-row items-center justify-between">
            <h2 className="text-xl font-bold" style={{ color: 'var(--nad-t1)' }}>Quotations</h2>
            <Button
              onClick={handleNew}
              size="sm"
              style={{
                background: 'linear-gradient(to bottom right, rgba(59,130,246,0.25), rgba(250,204,21,0.18))',
                border: '1px solid rgba(59,130,246,0.30)',
                color: 'var(--nad-accent-txt)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to bottom right, rgba(59,130,246,0.35), rgba(250,204,21,0.28))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to bottom right, rgba(59,130,246,0.25), rgba(250,204,21,0.18))';
              }}
            >
              <Plus className="h-4 w-4 mr-1" /> New
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4" style={{ color: 'var(--nad-t4)' }} />
            <Input
              placeholder="Filter quotations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
              style={{
                background: 'var(--nad-s2)',
                borderColor: 'var(--nad-b1)',
                color: 'var(--nad-t1)',
              }}
            />
          </div>
        </div>
        <div className="flex-1 overflow-auto p-0">
          {isLoading ? (
            <div className="text-center p-4" style={{ color: 'var(--nad-t3)' }}>Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow style={{ background: 'var(--nad-s2)' }}>
                  <TableHead style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>Client / Attention</TableHead>
                  <TableHead style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>Machine</TableHead>
                  <TableHead className="w-[80px]" style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuotations.map((q) => (
                  <TableRow
                    key={q.id}
                    className="cursor-pointer"
                    style={{
                      background:
                        currentQuotation?.id === q.id
                          ? 'linear-gradient(to right, rgba(250,204,21,0.16), rgba(59,130,246,0.10))'
                          : 'transparent',
                      borderColor: 'var(--nad-b1)',
                    }}
                    onClick={() => handleEdit(q)}
                    onMouseEnter={(e) => {
                      if (currentQuotation?.id !== q.id) {
                        e.currentTarget.style.background = 'var(--nad-s2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentQuotation?.id !== q.id) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <TableCell style={{ borderColor: 'var(--nad-b1)' }}>
                      <div className="font-medium" style={{ color: 'var(--nad-t1)' }}>{q.client_name}</div>
                      <div className="text-xs" style={{ color: 'var(--nad-t3)' }}>{q.attention}</div>
                    </TableCell>
                    <TableCell className="text-xs" style={{ borderColor: 'var(--nad-b1)' }}>
                      <div style={{ color: 'var(--nad-t1)' }}>{q.machine_model}</div>
                      <div style={{ color: 'var(--nad-t3)' }}>{q.serial_number}</div>
                    </TableCell>
                    <TableCell style={{ borderColor: 'var(--nad-b1)' }}>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          style={{ color: 'var(--nad-neg)' }}
                          onClick={(e) => { e.stopPropagation(); handleDelete(q.id); }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredQuotations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8" style={{ color: 'var(--nad-t3)' }}>
                      No quotations found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Right Panel - Form (70%) */}
      <div
        className="w-[70%] flex flex-col h-full overflow-hidden rounded-2xl"
        style={{
          background: 'var(--nad-s1)',
          border: '1px solid var(--nad-b1)',
        }}
      >
        <div
          className="py-3 px-4 border-b flex flex-row justify-between items-center"
          style={{
            borderColor: 'var(--nad-b1)',
            background: 'linear-gradient(to right, rgba(59,130,246,0.08), rgba(250,204,21,0.04))',
          }}
        >
          <div className="text-sm" style={{ color: 'var(--nad-t3)' }}>
            {currentQuotation ? `Editing Quotation: ${currentQuotation.client_name}` : 'New Quotation Draft'}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              style={{
                background: 'rgba(59,130,246,0.08)',
                borderColor: 'rgba(59,130,246,0.28)',
                color: 'var(--nad-accent-txt)',
              }}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsTemplateModalOpen(true)}
              style={{
                background: 'rgba(250,204,21,0.08)',
                borderColor: 'rgba(250,204,21,0.28)',
                color: 'var(--nad-t2)',
              }}
            >
              <Settings className="h-4 w-4 mr-2" /> Edit Template
            </Button>
            <Button
              onClick={handleSave}
              size="sm"
              style={{
                background: 'linear-gradient(to bottom right, rgba(34,197,94,0.22), rgba(59,130,246,0.18))',
                border: '1px solid rgba(34,197,94,0.30)',
                color: 'var(--nad-accent-txt)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to bottom right, rgba(34,197,94,0.32), rgba(59,130,246,0.28))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to bottom right, rgba(34,197,94,0.22), rgba(59,130,246,0.18))';
              }}
            >
              <Save className="h-4 w-4 mr-2" /> Save Quotation
            </Button>
          </div>
        </div>
        <div
          className="flex-1 overflow-auto p-6 flex flex-col items-center"
          style={{
            background:
              'radial-gradient(800px 400px at 10% 0%, rgba(59,130,246,0.08) 0%, transparent 55%), radial-gradient(800px 400px at 100% 100%, rgba(250,204,21,0.08) 0%, transparent 55%), var(--nad-s2)',
          }}
        >
          <div ref={componentRef}>
            {getPages().map((pageData, index) => renderPage(pageData, index))}
          </div>
        </div>
      </div>

      {/* Template Settings Modal */}
      <Dialog open={isTemplateModalOpen} onOpenChange={setIsTemplateModalOpen}>
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          style={{
            background: 'var(--nad-panel-bg)',
            border: '1px solid var(--nad-b1)',
            color: 'var(--nad-t1)',
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--nad-t1)' }}>Edit Quotation Template</DialogTitle>
            <DialogDescription style={{ color: 'var(--nad-t3)' }}>
              Modify the default text for new quotations. These changes will apply to all future drafts.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label style={{ color: 'var(--nad-t2)' }}>Salutation</Label>
              <Input 
                value={templateData.salutation} 
                onChange={(e) => setTemplateData(prev => ({ ...prev, salutation: e.target.value }))}
                style={{
                  background: 'var(--nad-s2)',
                  borderColor: 'var(--nad-b1)',
                  color: 'var(--nad-t1)',
                }}
              />
            </div>
            
            <div className="grid gap-2">
              <Label style={{ color: 'var(--nad-t2)' }}>Intro Text</Label>
              <Textarea 
                value={templateData.intro_text} 
                onChange={(e) => setTemplateData(prev => ({ ...prev, intro_text: e.target.value }))}
                style={{
                  background: 'var(--nad-s2)',
                  borderColor: 'var(--nad-b1)',
                  color: 'var(--nad-t1)',
                }}
              />
            </div>
            
            <div className="grid gap-2">
              <Label style={{ color: 'var(--nad-t2)' }}>Notes</Label>
              <Textarea 
                className="min-h-[100px]"
                value={templateData.notes} 
                onChange={(e) => setTemplateData(prev => ({ ...prev, notes: e.target.value }))}
                style={{
                  background: 'var(--nad-s2)',
                  borderColor: 'var(--nad-b1)',
                  color: 'var(--nad-t1)',
                }}
              />
            </div>
            
            <div className="grid gap-2">
              <Label style={{ color: 'var(--nad-t2)' }}>Delivery Terms</Label>
              <Textarea 
                value={templateData.delivery_terms} 
                onChange={(e) => setTemplateData(prev => ({ ...prev, delivery_terms: e.target.value }))}
                style={{
                  background: 'var(--nad-s2)',
                  borderColor: 'var(--nad-b1)',
                  color: 'var(--nad-t1)',
                }}
              />
            </div>
            
            <div className="grid gap-2">
              <Label style={{ color: 'var(--nad-t2)' }}>Closing Text</Label>
              <Input 
                value={templateData.closing_text} 
                onChange={(e) => setTemplateData(prev => ({ ...prev, closing_text: e.target.value }))}
                style={{
                  background: 'var(--nad-s2)',
                  borderColor: 'var(--nad-b1)',
                  color: 'var(--nad-t1)',
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label style={{ color: 'var(--nad-t2)' }}>Prepared By</Label>
                <Input 
                  value={templateData.prepared_by || ''} 
                  onChange={(e) => setTemplateData(prev => ({ ...prev, prepared_by: e.target.value }))}
                  placeholder="Leave empty to use logged-in user's name"
                  style={{
                    background: 'var(--nad-s2)',
                    borderColor: 'var(--nad-b1)',
                    color: 'var(--nad-t1)',
                  }}
                />
              </div>

              <div className="grid gap-2">
                <Label style={{ color: 'var(--nad-t2)' }}>Prepared By Title</Label>
                <Input 
                  value={templateData.prepared_by_title} 
                  onChange={(e) => setTemplateData(prev => ({ ...prev, prepared_by_title: e.target.value }))}
                  style={{
                    background: 'var(--nad-s2)',
                    borderColor: 'var(--nad-b1)',
                    color: 'var(--nad-t1)',
                  }}
                />
              </div>
              
              <div className="grid gap-2">
                <Label style={{ color: 'var(--nad-t2)' }}>Prepared By Email</Label>
                <Input 
                  value={templateData.prepared_by_email} 
                  onChange={(e) => setTemplateData(prev => ({ ...prev, prepared_by_email: e.target.value }))}
                  placeholder="Leave empty to use user's email"
                  style={{
                    background: 'var(--nad-s2)',
                    borderColor: 'var(--nad-b1)',
                    color: 'var(--nad-t1)',
                  }}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label style={{ color: 'var(--nad-t2)' }}>Signature</Label>
              <div className="flex items-center gap-4">
                {templateData.signature_url ? (
                  <div
                    className="relative border rounded p-2"
                    style={{
                      background: 'var(--nad-s2)',
                      borderColor: 'var(--nad-b1)',
                    }}
                  >
                    <img 
                      src={templateData.signature_url} 
                      alt="Signature Preview" 
                      className="h-16 object-contain"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                      style={{
                        background: 'rgba(248,113,113,0.18)',
                        color: 'var(--nad-neg)',
                      }}
                      onClick={() => setTemplateData(prev => ({ ...prev, signature_url: '' }))}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm italic" style={{ color: 'var(--nad-t3)' }}>No signature uploaded</div>
                )}
                <div className="relative">
                  <input
                    type="file"
                    id="signature-upload"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={handleSignatureUpload}
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => document.getElementById('signature-upload').click()}
                    style={{
                      background: 'var(--nad-s2)',
                      borderColor: 'var(--nad-b1)',
                      color: 'var(--nad-t2)',
                    }}
                  >
                    Upload Signature
                  </Button>
                </div>
              </div>
              <p className="text-xs" style={{ color: 'var(--nad-t3)' }}>Upload a transparent PNG signature. It will overlap with the "Prepared By" name.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsTemplateModalOpen(false)}
              style={{
                background: 'var(--nad-s2)',
                borderColor: 'var(--nad-b1)',
                color: 'var(--nad-t2)',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveTemplate}
              style={{
                background: 'linear-gradient(to bottom right, rgba(59,130,246,0.25), rgba(250,204,21,0.18))',
                border: '1px solid rgba(59,130,246,0.30)',
                color: 'var(--nad-accent-txt)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to bottom right, rgba(59,130,246,0.35), rgba(250,204,21,0.28))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to bottom right, rgba(59,130,246,0.25), rgba(250,204,21,0.18))';
              }}
            >
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceLifecycleQuotationRequest;
